'use client';
import React, { useState } from 'react';
import { useModel } from '@/context/ModelContext';
import {
  computeModel,
  SCENARIO_DRIVERS,
  fmtCrShort,
  fmtPct,
  Assumptions,
  FinancialModel,
} from '@/lib/financial-model';
import {
  TrendingDown,
  Minus,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Minus as MinusIcon,
  Bookmark,
  Activity,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

type ScenarioKey = 'downside' | 'base' | 'upside';

const SCENARIO_META = {
  downside: {
    label: 'DOWNSIDE',
    icon: <TrendingDown size={13} />,
    color: 'var(--downside)',
    bg: 'var(--downside-bg)',
    border: 'rgba(224,82,82,0.25)',
    borderTop: 'var(--downside)',
  },
  base: {
    label: 'BASE CASE',
    icon: <Minus size={13} />,
    color: 'var(--gold)',
    bg: 'var(--base-bg)',
    border: 'rgba(201,168,76,0.25)',
    borderTop: 'var(--gold)',
  },
  upside: {
    label: 'UPSIDE',
    icon: <TrendingUp size={13} />,
    color: 'var(--upside)',
    bg: 'var(--upside-bg)',
    border: 'rgba(62,207,142,0.25)',
    borderTop: 'var(--upside)',
  },
};

interface KPIRow {
  label: string;
  key: string;
  format: (m: FinancialModel) => string;
  getValue: (m: FinancialModel) => number;
  higherIsBetter: boolean;
  section?: string;
}

const KPI_ROWS: KPIRow[] = [
  {
    section: 'Returns',
    label: 'Project IRR',
    key: 'irr',
    format: (m) => `${(m.projectIRR * 100).toFixed(1)}%`,
    getValue: (m) => m.projectIRR,
    higherIsBetter: true,
  },
  {
    label: 'Equity IRR',
    key: 'eirr',
    format: (m) => `${(m.equityIRR * 100).toFixed(1)}%`,
    getValue: (m) => m.equityIRR,
    higherIsBetter: true,
  },
  {
    label: 'NPV @ 14%',
    key: 'npv',
    format: (m) => fmtCrShort(m.projectNPV),
    getValue: (m) => m.projectNPV,
    higherIsBetter: true,
  },
  {
    label: 'Payback Period',
    key: 'payback',
    format: (m) =>
      m.paybackYearFractional > 0 ? `Yr ${m.paybackYearFractional.toFixed(1)}` : 'N/A',
    getValue: (m) => (m.paybackYearFractional > 0 ? m.paybackYearFractional : 99),
    higherIsBetter: false,
  },
  {
    section: 'Revenue',
    label: 'Year 1 Revenue',
    key: 'rev1',
    format: (m) => fmtCrShort(m.years[1]?.revenue ?? 0),
    getValue: (m) => m.years[1]?.revenue ?? 0,
    higherIsBetter: true,
  },
  {
    label: 'Year 2 Revenue',
    key: 'rev2',
    format: (m) => fmtCrShort(m.years[2]?.revenue ?? 0),
    getValue: (m) => m.years[2]?.revenue ?? 0,
    higherIsBetter: true,
  },
  {
    label: 'Year 5 Revenue',
    key: 'rev5',
    format: (m) => fmtCrShort(m.years[5]?.revenue ?? 0),
    getValue: (m) => m.years[5]?.revenue ?? 0,
    higherIsBetter: true,
  },
  {
    section: 'Profitability',
    label: 'Yr2 EBITDA',
    key: 'ebitda2',
    format: (m) => fmtCrShort(m.years[2]?.ebitda ?? 0),
    getValue: (m) => m.years[2]?.ebitda ?? 0,
    higherIsBetter: true,
  },
  {
    label: 'Yr2 EBITDA Margin',
    key: 'ebitdam2',
    format: (m) => fmtPct(m.years[2]?.ebitdaMarginPct ?? 0),
    getValue: (m) => m.years[2]?.ebitdaMarginPct ?? 0,
    higherIsBetter: true,
  },
  {
    label: 'Yr2 PAT',
    key: 'pat2',
    format: (m) => fmtCrShort(m.years[2]?.pat ?? 0),
    getValue: (m) => m.years[2]?.pat ?? 0,
    higherIsBetter: true,
  },
  {
    label: 'Yr2 PAT Margin',
    key: 'patm2',
    format: (m) => fmtPct(m.years[2]?.patMarginPct ?? 0),
    getValue: (m) => m.years[2]?.patMarginPct ?? 0,
    higherIsBetter: true,
  },
  {
    section: 'Debt Service',
    label: 'DSCR (Year 2)',
    key: 'dscr2',
    format: (m) => `${(m.years[2]?.dscr ?? 0).toFixed(2)}x`,
    getValue: (m) => m.years[2]?.dscr ?? 0,
    higherIsBetter: true,
  },
  {
    label: 'DSCR (Year 3)',
    key: 'dscr3',
    format: (m) => `${(m.years[3]?.dscr ?? 0).toFixed(2)}x`,
    getValue: (m) => m.years[3]?.dscr ?? 0,
    higherIsBetter: true,
  },
  {
    section: 'Capital',
    label: 'Total Project Cost',
    key: 'tpc',
    format: (m) => fmtCrShort(m.totalProjectCost),
    getValue: (m) => m.totalProjectCost,
    higherIsBetter: false,
  },
  {
    label: 'Yr2 Gross Margin',
    key: 'gm2',
    format: (m) => fmtPct(m.years[2]?.grossMarginPct ?? 0),
    getValue: (m) => m.years[2]?.grossMarginPct ?? 0,
    higherIsBetter: true,
  },
];

function DeltaBadge({
  base,
  compare,
  higherIsBetter,
}: {
  base: number;
  compare: number;
  higherIsBetter: boolean;
}) {
  if (base === 0)
    return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.65rem' }}>—</span>;
  const delta = compare - base;
  const pct = (delta / Math.abs(base)) * 100;
  const isPositive = higherIsBetter ? delta > 0 : delta < 0;
  const isNeutral = Math.abs(pct) < 0.5;

  if (isNeutral) {
    return (
      <span
        className="delta-neutral inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm"
        style={{ fontSize: '0.6rem', fontWeight: 700 }}
      >
        <MinusIcon size={9} />
        {pct.toFixed(1)}%
      </span>
    );
  }

  return (
    <span
      className={isPositive ? 'delta-up' : 'delta-down'}
      style={{
        fontSize: '0.6rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        padding: '2px 6px',
        borderRadius: '2px',
      }}
    >
      {delta > 0 ? <ArrowUpRight size={9} /> : <ArrowDownRight size={9} />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--divider)',
        borderRadius: '4px',
        padding: '10px 14px',
      }}
    >
      <p
        style={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 6,
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-2" style={{ marginBottom: 2 }}>
          <div
            style={{ width: 8, height: 8, borderRadius: 1, background: p.color, flexShrink: 0 }}
          />
          <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>{p.name}:</span>
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            ₹{p.value?.toFixed(2)} Cr
          </span>
        </div>
      ))}
    </div>
  );
};


export default function ScenarioComparisonContent() {
  const { assumptions: liveAssumptions, savedScenarios } = useModel();
  
  const [scenarioA, setScenarioA] = useState<string>('downside');
  const [scenarioB, setScenarioB] = useState<string>('upside');
  const [tphA, setTphA] = useState<number>(liveAssumptions.tphCapacity);
  const [tphB, setTphB] = useState<number>(liveAssumptions.tphCapacity);

  const scenarioOptions = React.useMemo(() => {
    return [
      { id: 'live', label: 'Live Assumptions', icon: <Activity size={12} />, color: 'var(--accent)' },
      { id: 'downside', label: 'Downside Case', icon: <TrendingDown size={12} />, color: 'var(--downside)' },
      { id: 'base', label: 'Base Case', icon: <Minus size={12} />, color: 'var(--gold)' },
      { id: 'upside', label: 'Upside Case', icon: <TrendingUp size={12} />, color: 'var(--upside)' },
      ...Object.keys(savedScenarios).map(k => ({
        id: k, label: k, icon: <Bookmark size={12} />, color: 'var(--text-primary)'
      }))
    ];
  }, [savedScenarios]);

  function getModelFor(s: string, tphOverride: number): FinancialModel {
    let a = { ...liveAssumptions };
    if (s === 'live') {
      a = { ...liveAssumptions };
    } else if (s === 'downside' || s === 'base' || s === 'upside') {
      const d = SCENARIO_DRIVERS[s];
      a = {
        ...liveAssumptions,
        sellingPrice: d.sellingPrice,
        year1Utilisation: d.year1Utilisation,
        year2PlusUtilisation: d.year2PlusUtilisation,
        rawMaterialCost: d.rawMaterialCost,
        powerCost: d.powerCost,
      };
    } else if (savedScenarios[s]) {
      a = { ...savedScenarios[s] };
    }

    // Apply TPH Scaling
    if (tphOverride !== liveAssumptions.tphCapacity && tphOverride > 0) {
      const scaleFactor = tphOverride / liveAssumptions.tphCapacity;
      a.tphCapacity = tphOverride;
      a.machineryCapex = Math.round(a.machineryCapex * scaleFactor);
      a.civilCapex = Math.round(a.civilCapex * scaleFactor);
      a.electricalCapex = Math.round(a.electricalCapex * scaleFactor);
      a.contingencyCapex = Math.round(a.contingencyCapex * scaleFactor);
      a.workingCapital = Math.round(a.workingCapital * scaleFactor);
      const scaledProjectCost = a.machineryCapex + a.civilCapex + a.electricalCapex + a.contingencyCapex + a.workingCapital;
      a.equityAmount = Math.round(scaledProjectCost * 0.4);
      a.debtAmount = Math.round(scaledProjectCost * 0.6);
    }

    return computeModel(a);
  }

  const modelA = getModelFor(scenarioA, tphA);
  const modelB = getModelFor(scenarioB, tphB);

  const optA = scenarioOptions.find(o => o.id === scenarioA) || scenarioOptions[0];
  const optB = scenarioOptions.find(o => o.id === scenarioB) || scenarioOptions[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="gold-bar h-6" />
          <h1
            style={{
              fontSize: '1.375rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            Scenario Comparison Analysis
          </h1>
        </div>
        <p
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-tertiary)',
            letterSpacing: '0.04em',
            paddingLeft: '1rem',
          }}
        >
          Side-by-side KPI comparison with variance analysis · Select any two scenarios to compare
        </p>
      </div>

      {/* Selectors */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Scenario A', value: scenarioA, setter: setScenarioA, opt: optA, tph: tphA, setTph: setTphA },
          { label: 'Scenario B', value: scenarioB, setter: setScenarioB, opt: optB, tph: tphB, setTph: setTphB }
        ].map((col, idx) => (
          <div key={idx} className="exec-panel p-4" style={{ borderTop: `2px solid ${col.opt.color}` }}>
            <label style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
              {col.label}
            </label>
            <div className="flex items-center gap-3 mt-2">
              <select
                value={col.value}
                onChange={(e) => col.setter(e.target.value)}
                className="flex-1 p-2 rounded outline-none"
                style={{
                  background: 'var(--surface-3)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--divider)',
                  fontSize: '0.8rem',
                  fontWeight: 600
                }}
              >
                {scenarioOptions.map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <label style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                  TPH:
                </label>
                <input 
                  type="number"
                  value={col.tph}
                  onChange={(e) => col.setTph(Number(e.target.value))}
                  className="w-16 p-2 rounded outline-none text-right font-mono"
                  style={{
                    background: 'var(--surface-3)',
                    color: 'var(--gold)',
                    border: '1px solid var(--divider)',
                    fontSize: '0.8rem',
                    fontWeight: 700
                  }}
                  min={1}
                  step={1}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* KPI Comparison Table */}
      <div className="exec-panel">
        <div className="exec-panel-header">
          <h2
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            Full KPI Comparison
          </h2>
          <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
            Delta shows Scenario B relative to Scenario A
          </p>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full" style={{ minWidth: '700px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--divider)', background: 'var(--surface-2)' }}>
                <th className="text-left py-2.5 px-4" style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>KPI</th>
                <th className="py-2.5 px-4 text-center" style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: optA.color }}>{optA.label}</th>
                <th className="py-2.5 px-4 text-center" style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: optB.color }}>{optB.label}</th>
                <th className="py-2.5 px-4 text-center" style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Variance (B vs A)</th>
              </tr>
            </thead>
            <tbody>
              {KPI_ROWS.map((row, idx) => {
                const isSection = !!row.section;
                const valA = row.getValue(modelA);
                const valB = row.getValue(modelB);
                return (
                  <React.Fragment key={`kpi-row-${row.key}`}>
                    {isSection && (
                      <tr style={{ background: 'var(--surface-2)' }}>
                        <td colSpan={4} className="px-4 py-1.5" style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)' }}>
                          {row.section}
                        </td>
                      </tr>
                    )}
                    <tr style={{ borderBottom: '1px solid var(--divider)', background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                      <td className="py-2.5 px-4" style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-secondary)' }}>{row.label}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: optA.color }}>
                          {row.format(modelA)}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: optB.color }}>
                          {row.format(modelB)}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <DeltaBadge base={valA} compare={valB} higherIsBetter={row.higherIsBetter} />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
